import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import AnimatedPopup from '../../../../components/AnimatedPopup/AnimatedPopup';
import { clientConfig } from '../../../../config';
import { ChooseTreatmentContext, Product, ProductItem } from '../FreeDosageLayoutV2';
import { calculatePricePerGram, formatCannabisDisplayData, pricesForSelectedShippingMethod } from '../../helpersV2';
import { getPrice } from '../../../../utils/translator';
import * as constants from '../constants';
import { PharmacyMap } from '../../../../components/PharmacyPicker';
import { PharmacySelectionPanel } from '../../../../components/PharmacyPicker';
import { useAppContext } from '../../../../providers/AppProvider';
import { PharmacyCard } from '../../../../components/PharmacyPicker';
import { PharmacySearch } from '../../../../components/PharmacyPicker';
import * as translator from '../../../../utils/translator';
import { isFavoriteProductCatalogId, toggleFavoriteProductCatalogId } from '../../../../utils/favoritesStorage';
import { MainTracker } from '../../../../PixelTrackers/MainTracker';

// strip HTML tags
const stripHtml = (html: string): string =>
  (typeof window === 'undefined'
    ? html.replace(/<[^>]*>/g, '')
    : new DOMParser().parseFromString(html, 'text/html').body.textContent) || '';

const COLLAPSED_MAX_HEIGHT = 40;      // ~2 lines with text-sm/leading-relaxed
const SHOW_MORE_LEN_THRESHOLD = 160;  // show button only if content is long
const ProductDetailsCard: React.FC<{
  isVisible: boolean;
  onClose: () => void;
  product: Product;
  onAddToOrder: (quantity: number, localSelectedStockCode: string) => void;
  stocksLocations?: { [stockCode: string]: { name: string, address: string; location?: { lat: number; lng: number }; is_external_pharmacy: boolean } };
}> = ({ isVisible, onClose, product, onAddToOrder, stocksLocations = {} }) => {

  const language = localStorage.getItem("language") || "de";
  const chooseTreatmentContext = useContext(ChooseTreatmentContext);
  const locale = chooseTreatmentContext?.cart?.locale_id || 'de-DE';
  const selectedService = chooseTreatmentContext?.selectedService;
  const allPharmaciesLocations = chooseTreatmentContext?.allPharmaciesLocations;
  const [isExpanded, setIsExpanded] = useState(false);
  const selectedProductItem = chooseTreatmentContext?.selectedProductItems.find((item) => item.catalog_id === product.catalog_id);
  const defaultProductItemIndex = chooseTreatmentContext?.defaultProductItemIndex(product) || 0;
  const [currentProductItem, setCurrentProductItem] = useState<ProductItem>(selectedProductItem || product.items[defaultProductItemIndex]);
  const [localSelectedStockCode, setLocalSelectedStockCode] = useState<string | null>(currentProductItem?.selected_stock_code || chooseTreatmentContext?.selectedStockCode || null);
  const [mapSelectedStockCode, setMapSelectedStockCode] = useState<string | null>(null);
  const [pharmacySearchQuery, setPharmacySearchQuery] = useState<string>("");
  const stockCodesInRange = chooseTreatmentContext?.stockCodesInRange || [];
  const shippingMethod = chooseTreatmentContext?.shippingMethod;
  const service = chooseTreatmentContext?.selectedService;
  const index = product.items.findIndex((item) => item.product_item_id === currentProductItem?.product_item_id) || 0;
  const isSelfPickup = shippingMethod === constants.PICKUP;
  const isPrescriptionOnly = service === constants.SERVICE_PRESCRIPTION_ONLY;
  const isProductInStock = product.in_stock || product.express_stock || product.self_pickup_stock;
  const stickyNotificationAlreadyShown = useRef(false);
  const additionalDataObj = typeof product.additional_data === 'string' ? JSON.parse(product.additional_data) : product.additional_data || {};
  const { thcDisplay, cbdDisplay, strain } = formatCannabisDisplayData(additionalDataObj);

  const [prevCatalogId, setPrevCatalogId] = useState(product.catalog_id);
  const [isFavorite, setIsFavorite] = useState(() => isFavoriteProductCatalogId(product.catalog_id));
  if (prevCatalogId !== product.catalog_id) {
    setPrevCatalogId(product.catalog_id);
    setIsFavorite(isFavoriteProductCatalogId(product.catalog_id));
  }

  const { setStickyNotification } = useAppContext();

  const handleFavoriteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const catalogId = product.catalog_id;
    const isNowFavorite = toggleFavoriteProductCatalogId(catalogId);
    setIsFavorite(isNowFavorite);
    if (isNowFavorite) {
      MainTracker.track("custom", "Add to favorites", {
        "first-time user": chooseTreatmentContext?.isReturningCustomer ? "no" : "yes"
      });
    } else {
      MainTracker.track("custom", "Remove from favorites", {});
    }
    chooseTreatmentContext?.onFavoriteChange();
  }, [product.catalog_id, chooseTreatmentContext?.isReturningCustomer, chooseTreatmentContext?.onFavoriteChange]);

  // Filter stock locations for this product
  const availableStockCodes = product.available_stocks?.map(stock => stock.stockCode) || [];
  const filteredStocksLocations = useMemo(() => {
    const filtered: { [stockCode: string]: { name: string, address: string; location?: { lat: number; lng: number }; is_external_pharmacy: boolean } } = {};
    availableStockCodes.forEach(stockCode => {
      if (stocksLocations[stockCode]) {
        filtered[stockCode] = {
          ...stocksLocations[stockCode],
          is_external_pharmacy: false
        };
      }
    });
    return filtered;
  }, [availableStockCodes, stocksLocations]);


  // Filter external pharmacies for prescription-only: only those that have this product
  const externalPharmaciesLocations = useMemo(() => {
    if (!allPharmaciesLocations || !product.available_external_pharmacies?.length) return {};
    const availableSet = new Set(product.available_external_pharmacies.map(String));
    return Object.fromEntries(
      Object.entries(allPharmaciesLocations).filter(
        ([stockCode, pharmacy]) =>
          pharmacy.is_external_pharmacy && availableSet.has(stockCode)
      )
    );
  }, [allPharmaciesLocations, product.available_external_pharmacies]);

  // Determine which pharmacies to show: only those that have this product in stock
  const pharmaciesToShow = selectedService === constants.SERVICE_PRESCRIPTION_ONLY
    ? externalPharmaciesLocations
    : filteredStocksLocations;

  const hasPharmaciesToShow = Object.keys(pharmaciesToShow).length > 0;

  const handleMapSelection = (stockCode: string | null) => {
    // When clicking a marker on the map, show it in the modal and as the search result
    setMapSelectedStockCode(stockCode);
    if (stockCode && pharmaciesToShow[stockCode]) {
      setPharmacySearchQuery(pharmaciesToShow[stockCode].name);
    }
  }

  const handlePharmacySelect = (stockCode: string | null) => {
    // This is called from the "Choose Pharmacy" button
    // to actually select the pharmacy for the order
    if (isPrescriptionOnly) {
      chooseTreatmentContext?.setSelectedStockCode(stockCode);
    }
    setLocalSelectedStockCode(stockCode);
    setMapSelectedStockCode(null); // Clear map selection when confirming
    setPharmacySearchQuery(""); // Close search results when pharmacy is chosen
    stickyNotificationAlreadyShown.current = true;
    // Clear the sticky notification when a pharmacy is selected
    if (stockCode) {
      setStickyNotification({ show: false, title: "", content: "", level: "", showInfoIcon: false, autoFade: false });
    }
  }

  const handlePharmacyDeselect = () => {
    setLocalSelectedStockCode(null);
    setMapSelectedStockCode(null);
    if (selectedService === constants.SERVICE_PRESCRIPTION_ONLY) {
      chooseTreatmentContext?.setProducts(products => products.map(product => ({
        ...product,
        isSelected: false,
        selected_stock_code: null,
        items: product.items.map(item => ({...item, selected_stock_code: null}))
      })));
      chooseTreatmentContext?.setSelectedProductItems([]);
    }
  }

  const thc = product.additional_data?.thc;
  const cbd = product.additional_data?.cbd;
  const genetic = product.additional_data?.genetic;

  const imageUrl = product.image
    ? `${clientConfig.IMAGES_URL}${product.image.base_path}/${encodeURIComponent(product.image.name)}`
    : null;

  const plainDescription = stripHtml(product.description);
  const shouldShowToggle = plainDescription.length > SHOW_MORE_LEN_THRESHOLD;

  const { packPrice: basePrice, minPricePerUnit: listedPricePerUnit } = currentProductItem
    ? pricesForSelectedShippingMethod({
      product,
      productItem: currentProductItem,
      shippingMethod: shippingMethod || constants.SHIPPING,
      stockCodesInRange,
      selectedStockCode: localSelectedStockCode,
    })
    : { packPrice: 0, minPricePerUnit: 0 };
  const selectedPackPerGram = currentProductItem
    ? calculatePricePerGram(basePrice, currentProductItem, product.quantity_type)
    : null;
  const pricePerUnit = selectedPackPerGram ?? listedPricePerUnit;

  // CSS mask to fade the last visible line (second/third line) into transparent
  const collapsedMaskStyle: React.CSSProperties = {
    maxHeight: COLLAPSED_MAX_HEIGHT,
    overflow: 'hidden',
    WebkitMaskImage: 'linear-gradient(180deg, rgba(0,0,0,1) 60%, rgba(0,0,0,0) 100%)',
    maskImage: 'linear-gradient(180deg, rgba(0,0,0,1) 60%, rgba(0,0,0,0) 100%)',
    WebkitMaskSize: '100% 100%',
    maskSize: '100% 100%',
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
  };


  const getMaxAvailableAmountItem = (product: Product) => {
    
    // Start from the highest quantity and work down to find the first available item
    if (shippingMethod === constants.EXPRESS_SHIPPING) {
      for (let i = product.items.length - 1; i >= 0; i--) {
        if (product.items[i].express_stock) {
          return product.items[i];
        }
      }
    } else if (shippingMethod === constants.SHIPPING) {
      for (let i = product.items.length - 1; i >= 0; i--) {
        if (product.items[i].in_stock) {
          return product.items[i];
        }
      }
    } else if (shippingMethod === constants.PICKUP) {
      for (let i = product.items.length - 1; i >= 0; i--) {
        if (product.items[i].self_pickup_stock) {
          return product.items[i];
        }
      }
    }
    return product.items[0];
  }

  const maxProductInStock = useMemo(() => getMaxAvailableAmountItem(product), [product])

  const isPreviousButtonDisabled = useMemo(() => {
      const lowestInStockIndex = product.items.findIndex((item) => item.in_stock);
      return index === lowestInStockIndex;
  }, [index, product])

  const isNextButtonDisabled = useMemo(() => {
    return index === product.items.length - 1 || (isProductInStock && !chooseTreatmentContext?.treatAllAsRegular && currentProductItem?.quantity >= maxProductInStock.quantity);
  }, [index, product, chooseTreatmentContext, currentProductItem, maxProductInStock])

  const addProductItem = () => {
    if (shippingMethod === constants.PICKUP && !localSelectedStockCode && !stickyNotificationAlreadyShown.current) {
      setStickyNotification({ show: true, title: translator.translate(language, "choose_treatment", "default/please_select_a_pharmacy"), content: translator.translate(language, "choose_treatment", "default/please_select_a_pharmacy_to_add_product_to_order"), level: "error", showInfoIcon: false, autoFade: true });
    }

    else {
      onAddToOrder(currentProductItem!.quantity, localSelectedStockCode || '');
      onClose();
    }
  };

  const nextProductItem = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();

    if (index === product.items.length - 1) {
        return;
    }

    // Prescription only logic
    if (!isProductInStock || chooseTreatmentContext?.treatAllAsRegular) {
      setCurrentProductItem(product.items[index + 1]);
        return;
    }


    // Prescription + Medication logic
    if (currentProductItem!.quantity < maxProductInStock.quantity) {
      let nextAvailableIndex = -1;

      if (shippingMethod === constants.EXPRESS_SHIPPING) {
        nextAvailableIndex = product.items.findIndex((item, idx) => idx > index && item.express_stock);
      } else if (shippingMethod === constants.SHIPPING) {
        nextAvailableIndex = product.items.findIndex((item, idx) => idx > index && item.in_stock);
      } else if (shippingMethod === constants.PICKUP) {
        nextAvailableIndex = product.items.findIndex((item, idx) => idx > index && item.self_pickup_stock);
      }

      if (nextAvailableIndex !== -1) {
        setCurrentProductItem(product.items[nextAvailableIndex]);
      }
    }
  }, [product, index, chooseTreatmentContext, currentProductItem, maxProductInStock])


  const previousProductItem = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();

      if (index === 0) {
          return;
      }

      // Prescription only logic
      if (!isProductInStock || chooseTreatmentContext?.treatAllAsRegular) {
        setCurrentProductItem(product.items[index - 1]);
          return;
      }

      // Prescription + Medication logic
      let targetIndex = -1;
      for (let i = index - 1; i >= 0; i--) {
        if (shippingMethod === constants.EXPRESS_SHIPPING) {
          if (product.items[i].express_stock) {
            targetIndex = i;
            break;
          }
        } else if (shippingMethod === constants.SHIPPING) {
          if (product.items[i].in_stock) {
            targetIndex = i;
            break;
          }
        } else if (shippingMethod === constants.PICKUP) {
          if (product.items[i].self_pickup_stock) {
            targetIndex = i;
            break;
          }
        }
      }
      if (targetIndex !== -1) {
        setCurrentProductItem(product.items[targetIndex]);
      }
  }, [product, index, chooseTreatmentContext, currentProductItem, maxProductInStock])

  const favoriteHeartButton = (
    <button
      type="button"
      aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      onClick={handleFavoriteClick}
      className="p-0 bg-transparent border-none cursor-pointer rounded focus:outline-none focus:ring-2 focus:ring-[#06D1A1] focus:ring-offset-1 w-6 h-6 flex items-center justify-center"
    >
      {isFavorite ? (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="#DC2626" />
        </svg>
      ) : (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 stroke-[#4D4D4D] stroke-[1.5]">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );

  return (
    <AnimatedPopup isVisible={isVisible} onClose={onClose} title="" shouldBackgroundClickClose={false} contentClassName="!pb-0 !max-h-[80vh]" headerLeftContent={favoriteHeartButton} >
      <div className="flex flex-col gap-2">
        {imageUrl && (
          <div className="flex justify-center">
            <img
              src={imageUrl}
              alt={product.title}
              className="w-36 h-36"
              onError={e => (e.currentTarget.style.display = 'none')}
            />
          </div>
        )}

        {/* title + price on one line */}
        <div className="flex items-start justify-between gap-4">

          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-bold text-[#0D2C54] leading-snug flex-1">
              {product.title}
            </h2>

            <span className="text-xs text-[#4D4D4D]">
              {strain && (
                  <span>{strain}</span>
              )}
            </span>
          </div>
          
          <div className="text-right shrink-0">
            {service === constants.SERVICE_PRESCRIPTION_AND_MEDICATION && (
              <p className="text-lg font-bold text-[#0D2C54] leading-tight">
                {getPrice(pricePerUnit, locale)} <span className="text-xs font-normal">pro {product.items[0]?.custom_quantity_title?.split(' ')?.[1]}</span>
              </p>
            )}
            {genetic && (
                <div className="inline-block mt-2 bg-[#EFDEFF] text-[#5435C3] text-sm font-medium rounded-md">
                  <span className="p-2">{genetic}</span>
                </div>
            )}
          </div>
          
         
        </div>

        {/* THC/CBD */}
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="w-full bg-gray-200 rounded-full h-[5px]">
              <div className="bg-secondary-surface h-[5px] rounded-full" style={{ width: `${Math.min(thc! * 20, 100)}%` }}  />
            </div>
            <p className="text-sm text-[#0D2C54] font-medium">THC: {thcDisplay}</p>
          </div>
          <div className="flex-1">
            <div className="w-full bg-gray-200 rounded-full h-[5px]">
              <div className="bg-secondary-surface h-[5px] rounded-full" style={{ width: `${Math.min(cbd! * 20, 100)}%` }}  />
            </div>
            <p className="text-sm text-[#0D2C54] font-medium">CBD: {cbdDisplay}</p>
          </div>
        </div>

        {/* Manufacturer */}
        {product.manufacturer_name && (
          <div className="flex flex-col gap-1">
            <span className="text-sm text-gray-500">{translator.translate(language, "choose_treatment", "default/manufacturer-name")}</span>
            <span className="text-sm font-bold text-[#0D2C54]">{product.manufacturer_name}</span>
          </div>
        )}

        {/* Description: collapsed with gradient mask + toggle */}
        <div className="flex flex-col gap-1">
          <div
            className="text-sm text-[#0D2C54] leading-relaxed"
            style={isExpanded ? undefined : collapsedMaskStyle}
          >
            {plainDescription}
          </div>

          {shouldShowToggle && (
            <div className="text-center">
              <button
                onClick={() => setIsExpanded(v => !v)}
                className="text-[#0A9281] hover:underline font-medium cursor-pointer border-none bg-transparent"
              >
                {isExpanded ? translator.translate(language, "choose_treatment", "default/show-less") : translator.translate(language, "choose_treatment", "default/show-more")}
              </button>
            </div>
          )}
        </div>

        {/* Pharmacy Selection Section - Only show for self-pickup */}
        {shippingMethod === constants.PICKUP && (
          <>

            {localSelectedStockCode && (
              <PharmacyCard
                pharmacyId={localSelectedStockCode}
                pharmacy={localSelectedStockCode ? stocksLocations[localSelectedStockCode] : null}
                onCancel={handlePharmacyDeselect}
              />
            )}

            {/* Map View */}
            {!localSelectedStockCode && hasPharmaciesToShow && (
              <div className="flex flex-col w-full gap-3">      
                <h3 className="text-sm font-medium font-roboto text-[#363636]">
                  {translator.translate(language, 'choose_treatment', 'default/choose_pharmacy')}:
                </h3>

                {/* Pharmacy Search */}
                <PharmacySearch
                  stockLocations={pharmaciesToShow}
                  onPharmacySelect={handlePharmacySelect}
                  onPharmacyHighlight={handleMapSelection}
                  highlightedStockCode={mapSelectedStockCode}
                  externalSearchQuery={pharmacySearchQuery}
                />

                <PharmacyMap 
                  allPharmaciesLocations={pharmaciesToShow}
                  selectedStockCode={mapSelectedStockCode}
                  highlightedStockCode={mapSelectedStockCode}
                  onPharmacySelect={handleMapSelection}
                  onPharmacyHighlight={handleMapSelection}
                  height={480}
                  showSelectButton={false}
                >
                  {/* Pharmacy List with Pricing */}
                  <PharmacySelectionPanel
                    pharmaciesLocations={pharmaciesToShow}
                    availableStocks={
                      selectedService === constants.SERVICE_PRESCRIPTION_ONLY 
                        ? (product.available_external_pharmacies || []).map(id => ({ stockCode: id, pricePerUnit: 0 }))
                        : product.available_stocks || []
                    }
                    selectedStockCode={localSelectedStockCode}
                    highlightedStockCode={mapSelectedStockCode}
                    onPharmacySelect={handlePharmacySelect}
                    product={product}
                  />
                </PharmacyMap>

              </div>
            )}
          </>
        )}

        {/* sticky footer */}
        <div
          className="border-t border-gray-200 bg-white"
          style={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            width: '100%',
            zIndex: 20,
          }}
        >
          <div className="flex items-center w-full h-full justify-between gap-4 pb-8 md:pb-4 pt-3">
            <div className="flex flex-row w-full gap-4">
              <button
                id="add_to_order"
                onClick={addProductItem}
                disabled={isSelfPickup && !localSelectedStockCode}
                className={`flex flex-col w-1/2 bg-primary-button-gradient text-white text-sm p-2 rounded-lg font-medium border-none justify-center items-center ${isSelfPickup && !localSelectedStockCode ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                style={{ height: '65px' }}
              >
                <span>
                  {translator.translate(language, "choose_treatment", "default/add_to_order")}
                </span>
                {service === constants.SERVICE_PRESCRIPTION_AND_MEDICATION && (
                  <span>
                    {getPrice(basePrice, locale)}
                  </span>
                )}
              </button>
              <div 
                className="flex flex-row w-1/2 justify-between items-center bg-white rounded-lg px-3 py-2"
                style={{ height: '65px', border: '1px solid #E4E4E4' }}
              >
                <button
                  id="sub_quant"
                  onClick={previousProductItem}
                  disabled={isPreviousButtonDisabled}
                  className={`w-10 h-10 text-2xl flex items-center justify-center border-none bg-transparent text-[#0D2C54] ${isPreviousButtonDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  –
                </button>
                <div className="text-[#0D2C54] text-center text-lg">{currentProductItem?.quantity} {currentProductItem.custom_quantity_title.split(' ')?.[1]}</div>
                <button
                  id="add_quant"
                  onClick={nextProductItem}
                  disabled={isNextButtonDisabled}
                  className={`w-10 h-10 text-2xl flex items-center justify-center border-none bg-transparent text-[#0D2C54] ${isNextButtonDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </AnimatedPopup>
  );
};

export default ProductDetailsCard;
